/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import Input from "components/Input";
import Button from "components/Button";
import reimbursementManager from "services/Reimbursement/reimbursementManager";
import adminConfigService, { toSelectOptions } from "services/AdminConfig/adminConfigService";
// Dynamic, admin-configured upload policy (allowed formats + count + size).
import { useUploadConfig } from "hooks/useUploadConfig";
import { ReimbursementResponse, ReimbursementFile } from "services/Reimbursement/reimbursementInterface";
import { dateFormate } from "util/Util";
import formatCurrency, { getCurrencySymbol, getCurrencyIcon, formatAmount } from "util/currency";
import { getImageUrl } from "util/googleDriveHelper";
import { useAppContext } from "contexts/useAppContext";
// Icon set reused from the established InfoView design language.
import { FaUser, FaCalendarAlt, FaFileAlt, FaTag, FaDownload, FaEye } from "react-icons/fa";
import { Loader } from "lucide-react";
// Reuse the ViewReimbursement attachment experience: thumbnail + lightbox preview.
import MediaPreview, { useMediaPreview, MediaThumbnail, MediaItem, triggerDownload } from "components/MediaPreview";

// Fallback upload policy used only while the admin-config call is in flight or
// if it fails. Live rules come from GET api/upload-config/REIMBURSEMENT.
const REIMBURSEMENT_UPLOAD_FALLBACK = {
  allowedFileTypes: ["JPG", "PNG", "JFIF", "PDF"],
  maxFiles: 3,
  maxFileSizeKb: 2048, // 2 MB
  maxFileSizeMb: 2,
};

const uid = () => Math.random().toString(36).slice(2, 10);

// Best-effort attachment type detection from a filename or URL (tolerates the
// presigned query string). Falls back to "image" for extension-less Drive URLs.
const isPdfLike = (value?: string | null) => !!value && /\.pdf(\?|$)/i.test(value);

// Build the existing-attachment list from whichever shape the API returns:
//  • new shape → `files: [{ id, url, originalName }]` (real ids for `fileIds`)
//  • legacy shape → `data: string | string[]` (URLs only; no ids → synthetic
//    negative ids so they still render, and are excluded from `fileIds`)
const buildExistingFiles = (row?: ReimbursementResponse): ReimbursementFile[] => {
  if (!row) return [];
  if (Array.isArray(row.files) && row.files.length) return row.files;

  const urls = Array.isArray(row.data) ? row.data : row.data ? [row.data] : [];
  return urls
    .filter((url): url is string => typeof url === "string" && !!url.trim())
    .map((url, index) => ({
      id: -(index + 1),
      url,
      originalName: row.dataOriginalName ?? null,
    }));
};

// A newly added (not-yet-persisted) attachment with its own upload state.
interface NewAttachment {
  id: string;
  file: File;
  previewUrl: string | null;
  s3Key: string | null;
  uploading: boolean;
  uploadError: string;
}

// Static fallback used while the API call is in-flight
const FALLBACK_TYPES = [
  { value: "Travel", label: "Travel" },
  { value: "Office", label: "Office" },
  { value: "Hardware", label: "Hardware" },
  { value: "Software", label: "Software" },
  { value: "Party", label: "Party" },
  { value: "Food", label: "Food" },
  { value: "Other", label: "Other" },
];

const validationSchema = yup.object({
  reimbursementType: yup.string().required("Reimbursement type is required"),
  status: yup.string().required("Status is required"),
  description: yup.string().required("Description is required")
    .max(200, "Description cannot be more than 200 characters"),
  comment: yup.string().notRequired()
    .max(200, "Comment cannot be more than 200 characters"),
  submitAmount: yup
    .number()
    .typeError("Amount is required")
    .required("Amount is required")
    .positive("Amount must be positive"),
  // Hidden form value carrying the record's submit date so spentDate can validate
  // against it (submit date itself is read-only and not user-editable).
  submitDate: yup.string().notRequired(),
  spentDate: yup
    .string()
    .required("Spent Date is required")
    // Spent date cannot be after the submit date. Both are "yyyy-MM-dd" strings,
    // which compare correctly lexicographically. Skips the check when either is
    // missing (e.g. submitDate is null in the response).
    .test(
      "spent-before-submit",
      "Spent date cannot be after submit date",
      function (spentVal) {
        const { submitDate } = this.parent;
        if (!spentVal || !submitDate) return true;
        return spentVal <= submitDate;
      }
    ),
  paidAmount: yup
    .number()
    .notRequired()
    .typeError("Value must be a number")
    .positive("Value must be greater than zero")
    .test(
      "is-valid-city",
      // Kept short so it stays on a single line and fits the reserved error slot
      // (a wrapped 2-line message grew the row and shrank the image above it).
      "Cannot exceed submitted amount",
      async function (inputVal) {
        if (!inputVal) return true;
        const submittedAmount = localStorage.getItem("submittedAmount");
        if (submittedAmount !== null) {
          const sAmount = JSON.parse(submittedAmount as string);
          if (sAmount && inputVal > sAmount) return false;
        }
        return true;
      }
    ),
});

interface IProps {
  onClose: () => void;
  selectedRow: ReimbursementResponse | undefined;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export const status = [
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "PENDING", label: "Pending" },
  { value: "PAID", label: "Paid" },
];

// ─── Small reusable SVGs ──────────────────────────────────────────────────────

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 3v6h6" />
    <text x="6.5" y="18" fontSize="4.5" fontWeight="bold" fill="currentColor" stroke="none">PDF</text>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="w-3.5 h-3.5 animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

// Status → badge colors, reused from InfoView so badges stay consistent app-wide.
const STATUS_COLORS: Record<string, string> = {
  INITIATED: "bg-primary/10 text-primary border-primary/20",
  APPROVED: "bg-success/10 text-success border-success/20",
  REJECTED: "bg-error/10 text-error border-error/20",
  PAID: "bg-secondary/10 text-secondary border-secondary/20",
  PENDING: "bg-warning/10 text-warning border-warning/20",
};

const statusBadgeCls = (s?: string) =>
  `rounded-full px-3 py-1 text-[11px] font-bold tracking-widest uppercase border ${STATUS_COLORS[s ?? ""] || "bg-slate-100 text-slate-600 border-slate-200"
  }`;

// Read-only detail field — icon chip + uppercase label + value, matching the
// InfoView card style. Used to present user-submitted data the admin can't edit.
const ReadField = ({
  icon,
  label,
  value,
  valueClass = "",
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  valueClass?: string;
}) => (
  <div className="flex items-start gap-3">
    <div className="p-2 rounded-lg bg-primary/10 text-primary mt-0.5 flex-shrink-0">{icon}</div>
    <div className="min-w-0">
      <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
        {label}
      </p>
      <div className={`text-sm text-slate-900 dark:text-white font-semibold break-words ${valueClass}`}>
        {value}
      </div>
    </div>
  </div>
);

// Shared read-only value box (matches the existing disabled-field styling).
const READONLY_BOX =
  "bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white text-sm rounded-lg p-2.5";
const FIELD_LABEL = "block text-sm font-medium text-gray-900 dark:text-white mb-1";

// Angled "photo stack" transforms — front card straight, the two behind fan out
// so overlapping receipts read as a pile (adjust angles here to taste).
const STACK_TRANSFORMS = [
  "rotate(0deg)",
  "rotate(8deg) translate(8px, 2px)",
  "rotate(-8deg) translate(-8px, 2px)",
];
// How many receipts are shown in the stack before collapsing the rest into "+N".
const MAX_STACK = 3;

// ─── Component ────────────────────────────────────────────────────────────────

const UpdateReimbursement = ({ onClose, selectedRow, onSuccess, onError }: IProps) => {
  // Admin-configured upload policy for reimbursement receipts (dynamic rules +
  // reusable validators) — replaces the previous hardcoded formats/size.
  const uploadPolicy = useUploadConfig("REIMBURSEMENT", REIMBURSEMENT_UPLOAD_FALLBACK);
  const [selectedType, setSelectedType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [comment, setComment] = useState("");
  const [approvedAmount, setApprovedAmout] = useState(0);
  const [differenceAmount, setDifferenceAmount] = useState(0);
  const [, setSubmitAmount] = useState(0);
  const [spentDate, setSpentDate] = useState("");
  const [description, setDescription] = useState("");

  // Dynamic type options loaded from AdminConfig API
  const [typeOptions, setTypeOptions] = useState<{ value: string; label: string }[]>(FALLBACK_TYPES);

  // File upload state (multi-file)
  const [storageType, setStorageType] = useState<string>("DRIVE");
  // Existing attachments (with ids) the user is keeping — drives `fileIds`.
  const [existingFiles, setExistingFiles] = useState<ReimbursementFile[]>([]);
  // Newly added files (uploaded to S3 eagerly, or sent as DRIVE parts on submit).
  const [newFiles, setNewFiles] = useState<NewAttachment[]>([]);
  const [fileError, setFileError] = useState("");
  // Loading flag for the view-only attachment download (admin review / read-only).
  const [galleryDownloading, setGalleryDownloading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const anyUploading = newFiles.some((f) => f.uploading);
  const totalFiles = existingFiles.length + newFiles.length;
  // True when the user added new files or removed any existing attachment.
  const hasFileChanges =
    newFiles.length > 0 ||
    existingFiles.length !== buildExistingFiles(selectedRow).length;

  const { user } = useAppContext();

  const isNonAdminEditable = user.role !== 2 && selectedRow?.status === "INITIATED";
  const isAdminEditable = user.role === 2;
  const canEdit = isAdminEditable || isNonAdminEditable;
  const canEditOwnFile = isNonAdminEditable || (user.role === 2 && user.displayName === selectedRow?.username);

  // CHANGE: the amount fields hardcoded FaDollarSign, so a claim raised in INR
  // (or any non-USD currency) still showed a "$" next to the ₹ amount. Derived
  // once from the record's own unit and reused by every amount field, matching
  // the Unit field and the Approve Amount prefix, which were already
  // currency-aware via getCurrencySymbol.
  const CurrencyIcon = getCurrencyIcon(selectedRow?.unit);

  // Symbol shown beside the read-only Difference Amount. Falls back to the INR
  // symbol so the box never renders an empty prefix slot, matching the default
  // formatCurrency/formatAmount apply when a record has no unit.
  const differenceSymbol = getCurrencySymbol(selectedRow?.unit) || getCurrencySymbol("INR");

  // ── Fetch dynamic reimbursement types on mount ──────────────────────────────
  useEffect(() => {
    // Use the centralized getter so the endpoint path lives in exactly one place.
    adminConfigService.getReimbursementTypes()
      .then(({ data }) => {
        if (data.length > 0) setTypeOptions(toSelectOptions(data));
      })
      .catch(() => { /* keep FALLBACK_TYPES */ });
  }, []);

  // Fetch storage type on mount
  useEffect(() => {
    reimbursementManager.getStorageType()
      .then(({ data }) => setStorageType(data.storage))
      .catch(() => setStorageType("DRIVE"));
  }, []);

  // Revoke object URLs of newly added files on unmount to avoid leaks.
  const newFilesRef = useRef<NewAttachment[]>([]);
  newFilesRef.current = newFiles;
  useEffect(() => {
    return () => {
      newFilesRef.current.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
    };
  }, []);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      reimbursementType: selectedRow?.type || "",
      status: selectedRow?.status || "",
      description: selectedRow?.description || selectedRow?.comment || "",
      comment: selectedRow?.comment || "",
      paidAmount: selectedRow?.paidAmount || selectedRow?.approveAmount || 0,
      submitAmount: selectedRow?.submitAmount || 0,
      spentDate: selectedRow?.spentDate
        ? new Date(selectedRow.spentDate).toISOString().split("T")[0]
        : "",
      // Read-only reference date used only by the spentDate validation.
      submitDate: selectedRow?.submitDate
        ? new Date(selectedRow.submitDate).toISOString().split("T")[0]
        : "",
    },
  });
  const { formState: { isDirty, isSubmitting } } = methods;

  // ── Form field handlers ────────────────────────────────────────────────────

  const onReimbursementChange = (selectedOp: any) => {
    setSelectedType(selectedOp.value);
    methods.setValue("reimbursementType", selectedOp.value, { shouldDirty: true, shouldValidate: true });
  };

  const onStatusChange = (selectedOp: any) => {
    setSelectedStatus(selectedOp.value);
    methods.setValue("status", selectedOp.value, { shouldDirty: true, shouldValidate: true });
  };

  const onCommentChange = (e: any) => {
    setComment(e.target.value);
    methods.setValue("comment", e.target.value, { shouldDirty: true, shouldValidate: true });
  };

  const onSubmitAmountChange = (e: any) => {
    const amount = e.target.value;
    // Keep an empty string (not undefined) when cleared so the input stays
    // controlled and can be fully emptied. Yup still blocks submit: "" casts to
    // NaN → typeError ("Amount is required"), and 0 fails the positive() rule.
    methods.setValue(
      "submitAmount",
      amount === "" ? ("" as any) : Number(amount),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  const onSpentDateChange = (e: any) => {
    const date = e.target.value;
    setSpentDate(date);
    methods.setValue("spentDate", date, { shouldDirty: true, shouldValidate: true });
  };

  const onDescriptionChange = (e: any) => {
    const desc = e.target.value;
    setDescription(desc);
    methods.setValue("description", desc, { shouldDirty: true, shouldValidate: true });
  };

  const onApprovedAmountChange = (e: any) => {
    const amount = e.target.value;
    if (amount === "") {
      setApprovedAmout(amount);
      methods.setValue("paidAmount", undefined, { shouldDirty: true, shouldValidate: true });
      setDifferenceAmount(selectedRow?.submitAmount || 0);
      return;
    }
    const numericAmount = Number(amount);
    setApprovedAmout(numericAmount);
    methods.setValue("paidAmount", Number(amount), { shouldDirty: true, shouldValidate: true });
    setDifferenceAmount((selectedRow?.submitAmount ?? 0) - numericAmount);
  };

  // ── File handlers ─────────────────────────────────────────────────────────

  // Eager S3 upload for a single new attachment; updates its slot by id.
  const uploadNewFileToS3 = (attachment: NewAttachment) => {
    reimbursementManager.getUploadUrl(attachment.file.name)
      .then(async ({ data }) => {
        const putRes = await fetch(data.uploadUrl, { method: "PUT", body: attachment.file });
        if (putRes.status !== 200) throw new Error("S3 upload failed");
        setNewFiles((prev) => prev.map((f) =>
          f.id === attachment.id ? { ...f, s3Key: data.key, uploading: false, uploadError: "" } : f));
      })
      .catch(() => {
        setNewFiles((prev) => prev.map((f) =>
          f.id === attachment.id ? { ...f, uploading: false, uploadError: "Upload failed, please re-add" } : f));
      });
  };

  // Add one or more files: validates size/type, drops duplicates, enforces the
  // per-reimbursement cap, then (S3 mode) kicks off eager uploads.
  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files;
    if (!picked || !picked.length) return;

    const remaining = uploadPolicy.maxFiles - (existingFiles.length + newFiles.length);
    if (remaining <= 0) {
      setFileError(`You can attach up to ${uploadPolicy.maxFiles} files`);
      e.target.value = "";
      return;
    }

    const accepted: NewAttachment[] = [];
    let lastError = "";

    for (const file of Array.from(picked)) {
      if (accepted.length >= remaining) break;
      // Validate against the admin-configured allowed formats and max size.
      const validationError = uploadPolicy.validateFile(file);
      if (validationError) { lastError = validationError; continue; }
      // Skip duplicates (same name + size) against already-added new files.
      if (newFiles.some((f) => f.file.name === file.name && f.file.size === file.size)) continue;
      accepted.push({
        id: uid(),
        file,
        // Object URL for every type (not just images) so the lightbox can also
        // preview newly-added PDFs. Revoked on remove/unmount (see cleanup).
        previewUrl: URL.createObjectURL(file),
        s3Key: null,
        uploading: storageType === "S3",
        uploadError: "",
      });
    }

    if (accepted.length) {
      setNewFiles((prev) => [...prev, ...accepted]);
      setFileError("");
      if (storageType === "S3") accepted.forEach(uploadNewFileToS3);
    }
    if (lastError) setFileError(lastError);

    e.target.value = "";
  };

  // Remove a newly added (not-yet-saved) file.
  const removeNewFile = (id: string) => {
    setNewFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((f) => f.id !== id);
    });
  };

  // Drop an existing attachment from the keep-list (won't be in `fileIds`).
  const removeExistingFile = (id: number) => {
    setExistingFiles((prev) => prev.filter((f) => f.id !== id));
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const onSubmit = (data: any) => {
    // Guard: an editable user must keep at least one attachment as evidence.
    if (canEditOwnFile && totalFiles === 0) {
      setFileError("At least one attachment is required");
      return;
    }

    let payloadData: any;

    if (user.role === 2 && user.displayName !== selectedRow?.username) {
      const differenceAmount1 = (selectedRow?.submitAmount ?? 0) - (data.paidAmount || 0);
      payloadData = {
        id: selectedRow?.id || 0,
        approveAmount: data.paidAmount || selectedRow?.approveAmount || approvedAmount || 0,
        comment: data.comment || comment || selectedRow?.comment || "",
        description: selectedRow?.description || "",
        differenceAmount: differenceAmount1 || selectedRow?.differenceAmount || 0,
        lastModifiedDate: new Date().toISOString(),
        spentDate: selectedRow?.spentDate || "",
        status: data.status || selectedRow?.status,
        type: data.reimbursementType || selectedRow?.type,
        unit: selectedRow?.unit || "",
        data: null,
      };
    } else {
      payloadData = {
        id: selectedRow?.id || 0,
        type: data.reimbursementType || selectedType || selectedRow?.type,
        description: data.description || description || selectedRow?.description || selectedRow?.comment || "",
        spentDate: data.spentDate || spentDate || selectedRow?.spentDate || "",
        submitAmount: data.submitAmount ?? selectedRow?.submitAmount ?? 0,
        unit: selectedRow?.unit || "",
        lastModifiedDate: new Date().toISOString(),
        data: null,
      };
    }

    const formdata = new FormData();
    formdata.append("reimbursement", new Blob([JSON.stringify(payloadData)], { type: "application/json" }));

    // Only touch attachments when the user actually changed them. When the files
    // are untouched, we send just the payload (no newFiles parts, no query params)
    // so the backend keeps the existing attachments as they were.
    let fileParams: { deleteFileIds?: number[]; newKeys?: string[] } | undefined;
    if (hasFileChanges) {
      // Removed attachments = original files that are no longer kept. Legacy
      // records use synthetic negative ids (no real id) — exclude those.
      const keptIds = new Set(existingFiles.map((f) => f.id));
      const deleteFileIds = buildExistingFiles(selectedRow)
        .map((f) => f.id)
        .filter((fileId) => fileId > 0 && !keptIds.has(fileId));

      // New attachments: DRIVE → multipart `newFiles` parts; S3 → uploaded keys.
      const newKeys: string[] = [];
      if (storageType === "S3") {
        newFiles.forEach((f) => { if (f.s3Key) newKeys.push(f.s3Key); });
      } else {
        newFiles.forEach((f) => formdata.append("newFiles", f.file));
      }

      fileParams = { deleteFileIds, newKeys };
    }

    reimbursementManager.updateReimbursement(formdata, fileParams)
      .then((res) => {
        if (res.status === 200) onSuccess("Reimbursement is Updated successfully");
        else onError("Something went wrong");
      })
      .catch((err) => {
        console.log(`error updating ==> ${err}`);
        onError(err.response.data || "Something went wrong");
      })
      .finally(() => onClose());
  };

  // ── Sync selectedRow → form state ────────────────────────────────────────

  useEffect(() => {
    if (!selectedRow) return;

    setSelectedType(selectedRow.type);

    // Guarantee the stored type is always an option — handles cases where the
    // type was renamed or removed from AdminConfig after the reimbursement was saved.
    if (selectedRow.type) {
      setTypeOptions((prev) =>
        prev.find((o) => o.value === selectedRow.type)
          ? prev
          : [{ value: selectedRow.type, label: selectedRow.type }, ...prev]
      );
    }
    setSelectedStatus(selectedRow.status);
    setComment(selectedRow.comment);

    const initApproved = selectedRow?.paidAmount || selectedRow.approveAmount || 0;
    setApprovedAmout(initApproved);
    setDifferenceAmount(selectedRow.differenceAmount);
    setSubmitAmount(selectedRow.submitAmount || 0);
    setSpentDate(selectedRow.spentDate ? new Date(selectedRow.spentDate).toISOString().split("T")[0] : "");
    const initDesc = selectedRow.description || selectedRow.comment || "";
    setDescription(initDesc);
    localStorage.setItem("submittedAmount", selectedRow.submitAmount.toString());

    methods.reset({
      reimbursementType: selectedRow.type,
      status: selectedRow.status,
      description: initDesc,
      comment: selectedRow.comment || "",
      paidAmount: initApproved,
      submitAmount: selectedRow.submitAmount || 0,
      spentDate: selectedRow.spentDate ? new Date(selectedRow.spentDate).toISOString().split("T")[0] : "",
      // Read-only reference date used only by the spentDate validation.
      submitDate: selectedRow.submitDate ? new Date(selectedRow.submitDate).toISOString().split("T")[0] : "",
    });

    // Reset attachment state: keep all current files, clear any staged new ones.
    setNewFiles((prev) => {
      prev.forEach((f) => { if (f.previewUrl) URL.revokeObjectURL(f.previewUrl); });
      return [];
    });
    setExistingFiles(buildExistingFiles(selectedRow));
    setFileError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [selectedRow, methods]); // eslint-disable-line

  // Shared select classNames
  const selectCls = {
    control: ({ isFocused, isDisabled }: any) =>
      `!rounded-lg !border-gray-300 dark:!border-gray-600 !bg-gray-50 dark:!bg-gray-700 !min-h-[42px] ${isFocused ? "!ring-2 !ring-blue-500 !border-blue-500" : ""} ${isDisabled ? "!opacity-100" : ""}`,
    placeholder: () => "!text-slate-400 dark:!text-gray-400 !text-sm",
    singleValue: () => "!text-gray-900 dark:!text-white !text-sm",
    menu: () => "!bg-white dark:!bg-slate-900 !border !border-slate-200 dark:!border-slate-700 !rounded-xl !shadow-xl !overflow-hidden",
    option: ({ isFocused, isSelected }: any) =>
      `!text-sm ${isSelected ? "!bg-primary !text-white" : isFocused ? "!bg-primary/10 !text-primary" : "!text-slate-700 dark:!text-slate-300"}`,
  };

  // ── Layout modes (mutually exclusive) ───────────────────────────────────────
  // Submitter editing their OWN record (non-admin INITIATED, or an admin viewing
  // their own) → full edit form. An admin can never "approve" their own expense.
  const isSubmitterEditing = canEditOwnFile;
  // Admin reviewing SOMEONE ELSE's record → read-only details + review controls.
  const isAdminReviewing = isAdminEditable && user.displayName !== selectedRow?.username;
  // The admin may edit the review fields (approve amount / comment) freely, but
  // the Update button only enables once they actually change the status — a
  // status decision is the intended trigger for submitting an admin review.
  const statusChanged = selectedStatus !== (selectedRow?.status || "");
  // Owner viewing an already-processed record → everything locked.
  const isReadOnlyMode = !canEdit;

  // Reusable attachments block. Add/remove controls self-gate on `canEditOwnFile`,
  // so in review/read-only modes it renders as a view-only receipt gallery.
  const attachmentsBlock = (
    <div className="flex flex-col">
      <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
        Attachment{totalFiles > 0 ? ` (${totalFiles})` : ""}
      </label>

      {totalFiles > 0 ? (
        // Single column so file rows stay readable inside the narrow left panel.
        <div className="grid grid-cols-1 gap-2">
          {/* Existing attachments (kept unless removed) */}
          {existingFiles.map((f, exIdx) => {
            const showImage = !isPdfLike(f.originalName) && !isPdfLike(f.url);
            return (
              <div key={`ex-${f.id}`} className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/40">
                <button
                  type="button"
                  onClick={() => openPreview(exIdx)}
                  title="Preview"
                  className="flex-shrink-0 w-12 h-12 rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700 hover:ring-2 hover:ring-blue-400 transition"
                >
                  {showImage ? (
                    <img src={getImageUrl(f.url)} alt={f.originalName ?? "attachment"} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                      <PdfIcon />
                    </div>
                  )}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate leading-tight">
                    {f.originalName || "Attachment"}
                  </p>
                  <button
                    type="button"
                    onClick={() => openPreview(exIdx)}
                    className="text-xs text-blue-500 hover:underline mt-0.5 inline-flex items-center gap-1"
                  >
                    <FaEye size={10} /> Preview
                  </button>
                </div>
                {canEditOwnFile && (
                  <button type="button" onClick={() => removeExistingFile(f.id)} title="Remove attachment"
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}

          {/* Newly added attachments (not yet saved) */}
          {newFiles.map((f, nIdx) => {
            const showImage = f.file.type.startsWith("image/");
            const previewIdx = existingFiles.length + nIdx;
            return (
              <div key={`new-${f.id}`} className="flex items-center gap-3 p-2 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10">
                <button
                  type="button"
                  onClick={() => openPreview(previewIdx)}
                  title="Preview"
                  className="relative flex-shrink-0 w-12 h-12 rounded-md border border-gray-200 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-700 hover:ring-2 hover:ring-blue-400 transition"
                >
                  {showImage && f.previewUrl ? (
                    <img src={f.previewUrl} alt={f.file.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
                      <PdfIcon />
                    </div>
                  )}
                  <span className="absolute top-0.5 left-0.5 bg-blue-600 text-white text-[8px] font-bold px-1 rounded leading-tight">
                    NEW
                  </span>
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate leading-tight">
                    {f.file.name}
                  </p>
                  {f.uploading ? (
                    <p className="flex items-center gap-1 text-xs text-blue-500 mt-0.5">
                      <SpinnerIcon /> Uploading…
                    </p>
                  ) : f.uploadError ? (
                    <p className="text-xs text-error mt-0.5">{f.uploadError}</p>
                  ) : (
                    <button
                      type="button"
                      onClick={() => openPreview(previewIdx)}
                      className="text-xs text-blue-500 hover:underline mt-0.5 inline-flex items-center gap-1"
                    >
                      <FaEye size={10} /> Preview
                    </button>
                  )}
                </div>
                {canEditOwnFile && (
                  <button type="button" onClick={() => removeNewFile(f.id)} title="Remove attachment"
                    className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        !canEditOwnFile && <p className="text-sm text-gray-400 italic">No attachment</p>
      )}

      {/* Add control + help/error (own-editable only) */}
      {canEditOwnFile ? (
        <>
          {totalFiles < uploadPolicy.maxFiles && (
            <label className="cursor-pointer self-start mt-2">
              <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-500 text-sm font-medium text-gray-500 dark:text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/10 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {totalFiles > 0 ? "Add More Files" : "Attach Files"}
              </div>
              <input ref={fileInputRef} type="file" multiple accept={uploadPolicy.accept}
                className="sr-only" onChange={handleAddFiles} />
            </label>
          )}
          <div className="h-4 mt-1">
            {totalFiles === 0
              ? <p className="text-xs font-medium text-error leading-tight">At least one attachment is required</p>
              : fileError
                ? <p className="text-xs font-medium text-error leading-tight">{fileError}</p>
                : <p className="text-xs text-gray-400 dark:text-gray-500 leading-tight">{uploadPolicy.helpText}</p>
            }
          </div>
        </>
      ) : (
        <div className="h-4 mt-0.5" />
      )}
    </div>
  );

  // ── View-only attachment gallery (admin review / read-only) ─────────────────
  // Mirrors the ViewReimbursement (InfoView) experience: a clickable thumbnail
  // per receipt that opens the shared MediaPreview lightbox, plus a download.
  // Existing receipts first, then any newly-added (not-yet-saved) files. Order is
  // kept stable so the attachment rows can open the lightbox by index:
  //   existing → openPreview(exIdx)   ·   new → openPreview(existingFiles.length + nIdx)
  // The record's MIME hint (like InfoView) makes image/PDF receipts render a real
  // thumbnail; new files carry their own File.type.
  const galleryItems: MediaItem[] = [
    ...existingFiles.map((f) => ({
      url: f.url,
      name: f.originalName ?? undefined,
      mimeType: selectedRow?.dataType ?? undefined,
    })),
    ...newFiles.map((f) => ({
      url: f.previewUrl ?? "",
      name: f.file.name,
      mimeType: f.file.type,
    })),
  ];
  const { previewProps, open: openPreview } = useMediaPreview(galleryItems);

  const handleGalleryDownload = async (item: MediaItem) => {
    if (galleryDownloading) return;
    setGalleryDownloading(true);
    try {
      await triggerDownload(item);
    } finally {
      setGalleryDownloading(false);
    }
  };

  // First N receipts form the visible stack; the rest collapse into a "+N" badge.
  const stackItems = galleryItems.slice(0, MAX_STACK);
  const extraCount = galleryItems.length - stackItems.length;

  const previewGallery = (
    <div className="flex flex-col h-full min-h-0">
      <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-2 shrink-0">
        {galleryItems.length ? `Attachments (${galleryItems.length})` : "Attachments"}
      </p>
      {galleryItems.length ? (
        <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 py-2">
          {/* Angled, overlapping receipt stack. Front card opens the lightbox;
              cards behind fan out. A +N badge counts any receipts beyond MAX_STACK. */}
          <div className="relative w-40 h-40 sm:w-44 sm:h-44">
            {stackItems.map((item, i) => {
              const isFront = i === 0;
              return (
                <div
                  key={i}
                  className={`absolute inset-0 rounded-2xl overflow-hidden border-[3px] border-white dark:border-gray-700 shadow-md ring-1 ring-black/5 bg-white dark:bg-gray-800 transition-transform duration-200 ${isFront ? "hover:scale-[1.03]" : "pointer-events-none"
                    }`}
                  style={{ transform: STACK_TRANSFORMS[i] ?? "none", zIndex: stackItems.length - i }}
                >
                  <MediaThumbnail
                    item={item}
                    onClick={() => openPreview(0)}
                    className="!w-full !h-full !rounded-2xl !border-0 !shadow-none"
                  />
                </div>
              );
            })}
            {extraCount > 0 && (
              <span className="absolute -bottom-2 -right-2 z-40 flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-full bg-primary text-white text-sm font-bold shadow-lg ring-2 ring-white dark:ring-gray-800">
                +{extraCount}
              </span>
            )}
          </div>

          {/* Actions — preview opens the shared lightbox (with its own per-file download) */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openPreview(0)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg !bg-blue-700 hover:!bg-blue-800 !text-white text-xs font-semibold shadow-sm transition-colors"
            >
              <FaEye size={12} className="!text-white" />
              Preview{galleryItems.length > 1 ? ` (${galleryItems.length})` : ""}
            </button>
            <button
              type="button"
              onClick={() => handleGalleryDownload(stackItems[0])}
              disabled={galleryDownloading}
              title="Download first receipt"
              className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 dark:border-slate-500 bg-white dark:bg-gray-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-60 transition-colors"
            >
              {galleryDownloading ? <Loader size={14} className="animate-spin" /> : <FaDownload size={13} />}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center flex-1 min-h-[120px] rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
          <FaFileAlt className="text-slate-300 dark:text-slate-600 mb-2" size={28} />
          <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">No attachment</p>
        </div>
      )}
    </div>
  );

  return (
    <FormProvider {...methods}>
      <>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <div className="w-full max-w-5xl mx-auto bg-white dark:bg-gray-800 rounded-lg space-y-3">

            {/* Warning — owner viewing an already-processed record */}
            {isReadOnlyMode && (
              <div className="bg-warning/10 border-l-4 border-warning px-3 py-2 rounded-xl">
                <div className="flex items-start gap-2">
                  <svg className="h-4 w-4 text-warning flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-warning font-medium">
                    You cannot edit this reimbursement because the status is{" "}
                    <span className="font-bold underline">{selectedRow?.status}</span>.
                  </p>
                </div>
              </div>
            )}

            {/* ═══ Submitter edit form (own record, incl. an admin's own) ═══ */}
            {isSubmitterEditing && (
              // Height-bounded card: LEFT editable attachments · RIGHT editable
              // fields. Panels scroll internally only if constrained, so the
              // modal itself never scrolls and everything fits in view.
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[72vh]">
                <div className="grid grid-cols-1 md:grid-cols-3 flex-1 min-h-0">
                  {/* LEFT — editable attachments (add / remove / preview) */}
                  <div className="md:col-span-1 bg-slate-50 dark:bg-slate-800/50 p-3 border-b md:border-b-0 md:border-r border-slate-100 dark:border-slate-800 overflow-y-auto min-h-0">
                    {attachmentsBlock}
                  </div>

                  {/* RIGHT — editable fields */}
                  <div className="md:col-span-2 p-3 overflow-y-auto min-h-0">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                      Reimbursement Details
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {/* Type */}
                      <div className="flex flex-col">
                        <label className={FIELD_LABEL}>Reimbursement Type</label>
                        <Select
                          options={typeOptions}
                          value={
                            typeOptions.find((op) => op.value === selectedType) ??
                            (selectedType ? { value: selectedType, label: selectedType } : null)
                          }
                          onChange={onReimbursementChange}
                          classNames={selectCls}
                        />
                      </div>

                      {/* Submit Amount */}
                      <div className="flex flex-col">
                        <Input label="Submit Amount" name="submitAmount" type="number" onChange={onSubmitAmountChange} compact required />
                      </div>

                      {/* Spent Date */}
                      <div className="flex flex-col">
                        <Input type="date" name="spentDate" label="Spent Date" value={spentDate}
                          onChange={onSpentDateChange}
                          /* Cap the picker at the submit date (spent date can't be after it); fall back to today when the submit date is missing. */
                          max={selectedRow?.submitDate
                            ? new Date(selectedRow.submitDate).toISOString().split("T")[0]
                            : new Date().toISOString().split("T")[0]}
                          compact required />
                      </div>

                      {/* Submit Date (read-only) */}
                      <div className="flex flex-col">
                        <label className={FIELD_LABEL}>Submit Date</label>
                        <p className={READONLY_BOX}>{selectedRow && dateFormate(selectedRow.submitDate)}</p>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mt-2">
                      <Input type="textarea" name="description" label="Reimbursement Description" rows={2} value={description} onChange={onDescriptionChange} required />
                      {methods.formState.errors.description && (
                        <span className="text-red-500 text-xs font-medium leading-tight mt-0.5 block">
                          {methods.formState.errors.description.message as string}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ Admin reviewing someone else's record (two sections) ═══ */}
            {isAdminReviewing && (
              // Height-bounded card: the top row flexes & its panels scroll
              // internally if needed, so the modal itself never scrolls and the
              // admin controls stay pinned and fully visible.
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[70vh]">
                {/* Top row — LEFT: attachment thumbnails + preview · RIGHT: submitted details */}
                <div className="grid grid-cols-1 md:grid-cols-3 flex-1 min-h-0">
                  {/* LEFT — receipts (reference: ViewReimbursement) */}
                  <div className="md:col-span-1 bg-slate-50 dark:bg-slate-800/50 p-3 border-b md:border-b-0 md:border-r border-slate-100 dark:border-slate-800 overflow-y-auto min-h-0">
                    {previewGallery}
                  </div>

                  {/* RIGHT — user-submitted details, read only */}
                  <div className="md:col-span-2 p-3 overflow-y-auto min-h-0">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                          Reimbursement Type
                        </p>
                        <p className="text-base font-bold text-primary">{selectedRow?.type || "—"}</p>
                      </div>
                      <span className={statusBadgeCls(selectedRow?.status)}>{selectedRow?.status}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                      <ReadField icon={<FaUser size={14} />} label="Submitted by" value={selectedRow?.username || "—"} />
                      <ReadField icon={<CurrencyIcon size={14} />} label="Submit Amount" value={formatCurrency(selectedRow?.submitAmount, selectedRow?.unit)} />
                      <ReadField icon={<FaCalendarAlt size={14} />} label="Spent Date" value={selectedRow ? dateFormate(selectedRow.spentDate) : "—"} />
                      <ReadField icon={<FaCalendarAlt size={14} />} label="Submit Date" value={selectedRow ? dateFormate(selectedRow.submitDate) : "—"} />
                      {/* Show the currency symbol next to its code (e.g. "₹ (INR)") */}
                      <ReadField icon={<FaTag size={14} />} label="Unit" value={selectedRow?.unit ? `${getCurrencySymbol(selectedRow.unit)} (${selectedRow.unit})` : "₹ (INR)"} />
                      <div className="sm:col-span-2">
                        <ReadField
                          icon={<FaFileAlt size={14} />}
                          label="Description"
                          value={selectedRow?.description || selectedRow?.comment || "No description provided."}
                          valueClass="font-medium italic text-slate-700 dark:text-slate-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* BOTTOM — admin review controls, editable (pinned, always visible) */}
                <div className="border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-gray-800 p-3 shrink-0">
                  {/* <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Admin Review</h3> */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-2">
                    {/* Status */}
                    <div className="flex flex-col">
                      <label className={FIELD_LABEL}>Status</label>
                      <Select
                        options={status}
                        value={status.find((op) => op.value === selectedStatus)}
                        onChange={onStatusChange}
                        classNames={selectCls}
                        menuPlacement="top"
                      />
                    </div>

                    {/* Approve Amount — prefixed with the reimbursement's currency symbol */}
                    <div className="flex flex-col">
                      <Input label="Approve Amount" name="paidAmount" type="number" value={approvedAmount} onChange={onApprovedAmountChange} prefix={getCurrencySymbol(selectedRow?.unit)} compact />
                    </div>

                    {/* Difference (computed, read-only) */}
                    <div className="flex flex-col">
                      <label className={FIELD_LABEL}>Difference Amount</label>
                      {/* CHANGE: the symbol used to come baked into the formatted
                          string ("₹900.00"), leaving no gap before the value. It
                          is now a separate prefix using the same geometry as the
                          Input component's `prefix` (absolute, pl-3) and the same
                          derived padding, so this box lines up with the Approve
                          Amount field and multi-character symbols ("CHF", "CA$")
                          never overlap the value. */}
                      <p
                        className={`${READONLY_BOX} relative`}
                        style={{ paddingLeft: `calc(0.75rem + ${differenceSymbol.length}ch + 0.5rem)` }}
                      >
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500 dark:text-gray-400 text-sm font-medium pointer-events-none whitespace-nowrap">
                          {differenceSymbol}
                        </span>
                        {formatAmount(differenceAmount, selectedRow?.unit) || 0}
                      </p>
                    </div>
                  </div>

                  {/* Admin Comment */}
                  <div className="mt-2">
                    <Input type="textarea" name="comment" label="Admin Comment" rows={2} value={comment} onChange={onCommentChange} />
                    {methods.formState.errors.comment && (
                      <span className="text-red-500 text-xs font-medium leading-tight mt-0.5 block">
                        {methods.formState.errors.comment.message as string}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ═══ Read-only (owner, already processed) ═══ */}
            {isReadOnlyMode && (
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden flex flex-col max-h-[74vh]">
                <div className="grid grid-cols-1 md:grid-cols-3 flex-1 min-h-0">
                  {/* LEFT — receipts */}
                  <div className="md:col-span-1 bg-slate-50 dark:bg-slate-800/50 p-3 border-b md:border-b-0 md:border-r border-slate-100 dark:border-slate-800 overflow-y-auto min-h-0">
                    {previewGallery}
                  </div>

                  {/* RIGHT — details */}
                  <div className="md:col-span-2 p-3 overflow-y-auto min-h-0">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <p className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                          Reimbursement Type
                        </p>
                        <p className="text-base font-bold text-primary">{selectedRow?.type || "—"}</p>
                      </div>
                      <span className={statusBadgeCls(selectedRow?.status)}>{selectedRow?.status}</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                      <ReadField icon={<CurrencyIcon size={14} />} label="Submit Amount" value={formatCurrency(selectedRow?.submitAmount, selectedRow?.unit)} />
                      <ReadField icon={<FaCalendarAlt size={14} />} label="Spent Date" value={selectedRow ? dateFormate(selectedRow.spentDate) : "—"} />
                      <ReadField icon={<FaCalendarAlt size={14} />} label="Submit Date" value={selectedRow ? dateFormate(selectedRow.submitDate) : "—"} />
                      {selectedRow?.approveAmount != null && (
                        <ReadField icon={<CurrencyIcon size={14} />} label="Approved Amount" value={formatCurrency(selectedRow?.approveAmount, selectedRow?.unit)} valueClass="text-success" />
                      )}
                      {selectedRow?.comment && (
                        <ReadField icon={<FaFileAlt size={14} />} label="Admin Comment" value={selectedRow.comment} valueClass="font-medium italic text-slate-700 dark:text-slate-300" />
                      )}
                      <div className="sm:col-span-2">
                        <ReadField
                          icon={<FaFileAlt size={14} />}
                          label="Description"
                          value={selectedRow?.description || selectedRow?.comment || "No description provided."}
                          valueClass="font-medium italic text-slate-700 dark:text-slate-300"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Buttons ── */}
            <div className="flex justify-end gap-2 pt-1">
              <Button color="slate" className="!bg-slate-100 hover:!bg-slate-200 !text-slate-700" onClick={onClose} type="button">
                {isReadOnlyMode ? "Close" : "Cancel"}
              </Button>
              {!isReadOnlyMode && (
                <Button
                  type="submit"
                  disabled={
                    !canEdit ||
                    isSubmitting ||
                    anyUploading ||
                    (isAdminReviewing
                      ? !statusChanged
                      : (!isDirty && !hasFileChanges) || (canEditOwnFile && totalFiles === 0))
                  }
                  className="!text-white !bg-blue-700 hover:!bg-blue-800 focus:!ring-4 focus:!outline-none focus:!ring-blue-300 dark:!bg-blue-600 dark:hover:!bg-blue-700 dark:focus:!ring-blue-800"
                >
                  Update
                </Button>
              )}
            </div>

          </div>
        </form>

        {/* Shared lightbox — image zoom / PDF / video, reused from ViewReimbursement */}
        <MediaPreview {...previewProps} />
      </>
    </FormProvider>
  );
};

export default UpdateReimbursement;
